import React from "react";
import "./App.css";
import {
  Route,
  Routes,
  BrowserRouter,
  Outlet,
  Navigate,
  useLocation,
} from "react-router-dom";
import Cookies from "js-cookie";
import LoginLayout from "./components/Layout/LoginLayout";
import Login from "./pages/Auth/Login";
import ForgetPassword from "./pages/Auth/ForgetPassword";
import ResetPassword from "./pages/Auth/ResetPassword";
import Signup from "./pages/Auth/Signup";
import Dashboard from "./pages/Dashboard";
import Stores from "./pages/Stores/Stores";
import Users from "./pages/Users";
import AllUsers from "./pages/AllUsers";
import Plans from "./pages/Plans";
import PlansPricing from "./pages/PlansPricing/PlansPricing";
import CreatePlan from "./pages/PlansPricing/CreatePlan";
import PlanUsageStatistics from "./pages/PlansPricing/PlanUsageStatistics";
import SubscriberToolUsage from "./pages/PlansPricing/SubscriberToolUsage";
import Setting from "./pages/Setting";
// import CreateStore from "./pages/Stores/CreateStore";
import BulkUpload from "./pages/Products/BulkUpload";
import Products from "./pages/Products/Products";
import AddManual from "./pages/Products/AddManual";
import SignupLayout from "./components/Layout/SignupLayout";
import InviteSignup from "./pages/Auth/InviteSignup";
import InviteSignupUsers from "./pages/Auth/InviteSignupUsers";
import Merchants from "./pages/Merchants/Merchants";
import MerchantDashboard from "./pages/MerchantDashboard";
import UpdateStore from "./pages/Stores/UpdateStore";
import ReSubmitStore from "./pages/Stores/ReSubmitStore";
import { CreateStore } from "./pages/Stores/CreateStore";
import LibraryComponent from "./pages/Library/LibraryComponent";
import CategoriesComponent from "./pages/Categories/Categories";
import Campaigns from "./pages/Campaigns/Campaigns";
import Discounts from "./pages/Discounts/Discounts";
import Announcements from "./pages/Announcements/Announcements";
import Contacts from "./pages/Contacts/Contacts";
import SalesRequests from "./pages/SalesRequests/SalesRequests";
import SitePolicies from "./pages/SitePolicies/SitePolicies";
import Referrals from "./pages/Referrals";
import ChatbotChecker from "./pages/ChatbotChecker";
import CreateRoles from "./pages/CreateRoles";
import SallaAnalytics from "./pages/SallaAnalytics";
import OrganizationTeam from "./pages/Organization/OrganizationTeam";
import OrganizationGenerations from "./pages/Organization/OrganizationGenerations";
import OrgOwnerRoute from "./components/Organization/OrgOwnerRoute";
import AuthenticatedLayout from "./components/Layout/AuthenticatedLayout";
import PostHogAnalytics from "./pages/PostHogAnalytics";
import useAuth from "./services/useAuth";
import { jwtDecode } from "jwt-decode";
import { getUserCookies } from "./utils/Cookies";
import { useSelector, useDispatch } from "react-redux";
import { fetchUserDetails } from "./ReduxToolKit/Slice/UserSlice";
import {
  effectiveHighestRole,
  isProfileMarketPortalUser,
  getPostLoginPathFromProfile,
} from "./utils/modulePermissionSidebar";
import {
  ORGANIZATION_HOME_PATH,
  canAccessOrganizationRoutes,
  hasMerchantPortalAccess,
  isNoActiveMerchantPortalAccount,
  jwtIndicatesMerchantPortal,
  shouldWaitForOrganizationProbe,
} from "./utils/organizationAccess";
import { probeOrganizationOwner } from "./ReduxToolKit/Slice/OrganizationSlice";
import {
  getLocalizedPath,
  getPathLanguage,
  normalizeRoutePath,
} from "./utils/authLanguageRoutes";
import { getAppLanguage } from "./utils/getAppLanguage";
import SeoMeta from "./components/Common/SeoMeta";

function ProfileHomeCatchAll() {
  const profileUser = useSelector((s) => s.user.user);
  const location = useLocation();
  const lang = getPathLanguage(location.pathname) || getAppLanguage();
  return (
    <Navigate
      to={getLocalizedPath(getPostLoginPathFromProfile(profileUser), lang)}
      replace
    />
  );
}

function LocalizedPathRoute({ children }) {
  const location = useLocation();
  const pathLanguage = getPathLanguage(location.pathname);
  const effectiveLanguage = pathLanguage || getAppLanguage();
  const localizedPath = getLocalizedPath(location.pathname, effectiveLanguage);
  const currentPath = normalizeRoutePath(location.pathname);

  if (localizedPath !== currentPath) {
    return (
      <Navigate
        to={`${localizedPath}${location.search}${location.hash}`}
        replace
      />
    );
  }

  return children;
}

/** Navigate while preserving `/en` prefix for English. */
function LocalizedNavigate({ to, replace = false }) {
  const location = useLocation();
  const lang = getPathLanguage(location.pathname) || getAppLanguage();
  return <Navigate to={getLocalizedPath(to, lang)} replace={replace} />;
}

function AuthLandingRedirect() {
  const profileUser = useSelector((s) => s.user.user);
  const isOrgOwner = useSelector((s) => s.organization.isOwner);
  const location = useLocation();
  const lang = getPathLanguage(location.pathname) || getAppLanguage();
  const token = Cookies.get("token");
  if (canAccessOrganizationRoutes(profileUser, isOrgOwner)) {
    return <Navigate to={getLocalizedPath(ORGANIZATION_HOME_PATH, lang)} replace />;
  }
  if (!token) return <Navigate to={getLocalizedPath("/login", lang)} replace />;

  let target = getPostLoginPathFromProfile(profileUser);
  try {
    if (String(jwtDecode(token)?.market_role ?? "") === "2") {
      target = "/merchants";
    }
  } catch (_) {
    /* keep profile target */
  }
  return <Navigate to={getLocalizedPath(target, lang)} replace />;
}

function localizedRouteElements(path, element) {
  const englishPath = path === "/*" ? "/en/*" : path === "/" ? "/en" : `/en${path}`;

  return [
    <Route
      key={`base:${path}`}
      path={path}
      element={<LocalizedPathRoute>{element}</LocalizedPathRoute>}
    />,
    <Route
      key={`en:${path}`}
      path={englishPath}
      element={<LocalizedPathRoute>{element}</LocalizedPathRoute>}
    />,
  ];
}

function App() {
  const { highestRole, marketRole } = useAuth();
  const profileUser = useSelector((s) => s.user.user);

  const decodedToken = (() => {
    try {
      const userToken = getUserCookies()?.token;
      return userToken ? jwtDecode(userToken) : null;
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  })();

  const jwtHighestRole = decodedToken?.highest_role || highestRole;
  const currentMarketRole = decodedToken?.market_role || marketRole;
  const effectiveHighestRoleForRoutes = effectiveHighestRole(
    profileUser,
    jwtHighestRole,
  );
  /** Profile OWNER/VIEWER always uses the market super-admin route tree (not narrow JWT packs). */
  const skipNarrowMarketJwtBranch = isProfileMarketPortalUser(profileUser);

  const marketRoleStr = String(currentMarketRole ?? "");
  const isMarketRole = marketRoleStr === "1";
  const isMarketRoleTwo = marketRoleStr === "2";
  const isBusinessDeveloper = marketRoleStr === "3";

  return (
    <>
      <BrowserRouter>
        <SeoMeta />
        <Routes>
          {/* // Protected routes can be added below this */}
          <Route element={<Protected />}>
            <Route element={<AuthenticatedLayout />}>
              {localizedRouteElements(
                "/organization",
                <OrgOwnerRoute>
                  <OrganizationTeam />
                </OrgOwnerRoute>
              )}
              {localizedRouteElements(
                "/organization/generations",
                <OrgOwnerRoute>
                  <OrganizationGenerations />
                </OrgOwnerRoute>
              )}
              {/* Routes accessible to market_role === "1" */}
              {isMarketRole ? (
                <>
                  {localizedRouteElements("/dashboard", <Dashboard />)}
                  {localizedRouteElements("/posthog", <PostHogAnalytics />)}
                  {localizedRouteElements(
                    "/dashboard/merchant-analytics",
                    <MerchantDashboard />
                  )}
                  {localizedRouteElements("/all-users", <AllUsers />)}
                  {localizedRouteElements("/campaigns", <Campaigns />)}
                  {localizedRouteElements("/discounts", <Discounts />)}
                  {localizedRouteElements("/announcements", <Announcements />)}
                  {localizedRouteElements("/contacts", <Contacts />)}
                  {localizedRouteElements(
                    "/sales-requests",
                    <SalesRequests />
                  )}
                  {localizedRouteElements("/site-policies", <SitePolicies />)}
                  {localizedRouteElements("/setting", <Setting />)}
                  {localizedRouteElements("/*", <ProfileHomeCatchAll />)}
                </>
              ) : isBusinessDeveloper && !skipNarrowMarketJwtBranch ? (
                /* business developer: read-only dashboard + merchants + stores + merchant users */
                <>
                  {localizedRouteElements("/dashboard", <Dashboard />)}
                  {localizedRouteElements(
                    "/dashboard/merchant-analytics",
                    <MerchantDashboard />
                  )}
                  {localizedRouteElements("/merchants", <Merchants />)}
                  {localizedRouteElements("/stores", <Stores />)}
                  {localizedRouteElements("/users", <Users />)}
                  {localizedRouteElements("/setting", <Setting />)}
                  {localizedRouteElements("/*", <LocalizedNavigate to="/dashboard" replace />)}
                </>
              ) : isMarketRoleTwo && !skipNarrowMarketJwtBranch ? (
                /* market_role === "2" (Business Viewer): read-only Merchants, Stores, Merchant Users; land on /merchants */
                <>
                  {localizedRouteElements("/merchants", <Merchants />)}
                  {localizedRouteElements("/stores", <Stores />)}
                  {localizedRouteElements("/users", <Users />)}
                  {localizedRouteElements("/setting", <Setting />)}
                  {localizedRouteElements("/*", <LocalizedNavigate to="/merchants" replace />)}
                </>
              ) : (
                <>
                  {localizedRouteElements("/dashboard", <Dashboard />)}
                  {localizedRouteElements(
                    "/dashboard/merchant-analytics",
                    <MerchantDashboard />
                  )}
                  {localizedRouteElements(
                    "/dashboard/salla-analytics",
                    <SallaAnalytics />
                  )}
                  {localizedRouteElements("/products", <Products />)}
                  {localizedRouteElements(
                    "/products/bulk-upload/:storeId",
                    <BulkUpload />
                  )}
                  {localizedRouteElements(
                    "/products/add-manual/:storeId",
                    <AddManual />
                  )}
                  {localizedRouteElements(
                    "/products/update/:productId",
                    <AddManual />
                  )}
                  {localizedRouteElements("/stores", <Stores />)}
                  {localizedRouteElements(
                    "/stores/create-store",
                    <CreateStore />
                  )}
                  {localizedRouteElements(
                    "/stores/update-store/:storeId",
                    <UpdateStore />
                  )}
                  {localizedRouteElements(
                    "/stores/resubmit-store/:storeId",
                    <ReSubmitStore />
                  )}
                  {localizedRouteElements("/users", <Users />)}
                  {localizedRouteElements("/all-users", <AllUsers />)}
                  {localizedRouteElements(
                    "/create-roles",
                    effectiveHighestRoleForRoutes === "SUPERADMIN" ? (
                      <CreateRoles />
                    ) : (
                      <ProfileHomeCatchAll />
                    )
                  )}
                  {localizedRouteElements("/plans", <Plans />)}
                  {localizedRouteElements("/setting", <Setting />)}
                  {effectiveHighestRoleForRoutes === "SUPERADMIN" && (
                    <>
                      {localizedRouteElements("/posthog", <PostHogAnalytics />)}
                      {localizedRouteElements("/campaigns", <Campaigns />)}
                      {localizedRouteElements("/discounts", <Discounts />)}
                      {localizedRouteElements("/plans-pricing", <PlansPricing />)}
                      {localizedRouteElements(
                        "/plans-pricing/usage",
                        <PlanUsageStatistics />
                      )}
                      {localizedRouteElements(
                        "/plans-pricing/usage/tools",
                        <SubscriberToolUsage />
                      )}
                      {localizedRouteElements(
                        "/plans-pricing/create",
                        <CreatePlan />
                      )}
                      {localizedRouteElements(
                        "/plans-pricing/edit/:planId",
                        <CreatePlan />
                      )}
                      {localizedRouteElements("/announcements", <Announcements />)}
                      {localizedRouteElements("/library", <LibraryComponent />)}
                      {localizedRouteElements("/merchants", <Merchants />)}
                      {localizedRouteElements(
                        "/categories",
                        <CategoriesComponent />
                      )}
                      {localizedRouteElements("/contacts", <Contacts />)}
                      {localizedRouteElements(
                        "/sales-requests",
                        <SalesRequests />
                      )}
                      {localizedRouteElements(
                        "/site-policies",
                        <SitePolicies />
                      )}
                      {localizedRouteElements("/referrals", <Referrals />)}
                      {localizedRouteElements(
                        "/chatbot-checker",
                        <ChatbotChecker />
                      )}
                    </>
                  )}
                  {localizedRouteElements("/*", <ProfileHomeCatchAll />)}
                </>
              )}
            </Route>
          </Route>
          {/* // Non-Protected routes can be added below this */}
          <Route element={<LoginLayout />}>
            {localizedRouteElements("/login", <Login />)}
            {localizedRouteElements("/forget-password", <ForgetPassword />)}
            {localizedRouteElements("/reset-password", <ResetPassword />)}
          </Route>
          <Route element={<SignupLayout />}>
            {localizedRouteElements("/signup", <Signup />)}
            {localizedRouteElements("/invite-signup", <InviteSignup />)}
            {localizedRouteElements("/invite-signup-users", <InviteSignupUsers />)}
          </Route>
          {localizedRouteElements("/*", <AuthLandingRedirect />)}
        </Routes>
      </BrowserRouter>
    </>
  );
}

export default App;

const Protected = () => {
  const token = Cookies.get("token");
  const location = useLocation();
  const lang = getPathLanguage(location.pathname) || getAppLanguage();
  const loginPath = getLocalizedPath("/login", lang);
  const dispatch = useDispatch();
  const profileUser = useSelector((s) => s.user.user);
  const userLoading = useSelector((s) => s.user.loading);
  const isOrgOwner = useSelector((s) => s.organization.isOwner);
  const orgProbeLoading = useSelector((s) => s.organization.probeLoading);
  const [profileResolved, setProfileResolved] = React.useState(false);

  React.useEffect(() => {
    if (!token) return undefined;
    if (profileUser) {
      setProfileResolved(true);
      return undefined;
    }
    dispatch(fetchUserDetails())
      .unwrap()
      .catch(() => {})
      .finally(() => {
        setProfileResolved(true);
      });
    return undefined;
  }, [token, profileUser, dispatch]);

  React.useEffect(() => {
    if (!token || !profileResolved) return undefined;
    dispatch(probeOrganizationOwner(profileUser ?? undefined));
    return undefined;
  }, [token, profileResolved, profileUser, dispatch]);

  if (!token) {
    return <Navigate to={loginPath} replace />;
  }

  const waitingForProfile =
    jwtIndicatesMerchantPortal(token) && !profileUser && (userLoading || !profileResolved);

  if (waitingForProfile) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <span className="text-14 text-8C8C80">Loading…</span>
      </div>
    );
  }

  try {
    const decoded = jwtDecode(token);
    const currentTime = Date.now() / 1000;

    if (decoded.exp && decoded.exp < currentTime) {
      Cookies.remove("token");
      return <Navigate to={loginPath} replace />;
    }

    if (
      shouldWaitForOrganizationProbe(
        profileUser,
        token,
        isOrgOwner,
        orgProbeLoading,
      )
    ) {
      return (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "100vh" }}
        >
          <span className="text-14 text-8C8C80">Loading…</span>
        </div>
      );
    }

    const canAccess = hasMerchantPortalAccess(profileUser, token, isOrgOwner);

    if (canAccess) {
      return <Outlet />;
    }

    Cookies.remove("token");
    if (isNoActiveMerchantPortalAccount(profileUser, token, isOrgOwner)) {
      return (
        <Navigate to={`${loginPath}?reason=no_merchant_access`} replace />
      );
    }
    return <Navigate to={loginPath} replace />;
  } catch (err) {
    console.error("Invalid token");
    Cookies.remove("token");
    return <Navigate to={loginPath} replace />;
  }
};
